<script setup>
import { ref, onMounted, watch, nextTick, onUnmounted } from "vue";
import * as echarts from "echarts";
import "echarts/theme/dark.js";
import { useTheme } from "vuetify";

const props = defineProps({
  // Basic props
  isDaily: {
    type: Boolean,
    required: true,
  },
  isDev: {
    type: Boolean,
    default: false,
  },

  // Chart type and data
  chartType: {
    type: String,
    default: "bar",
  },
  filteredData: {
    type: Object,
    default: null,
  },

  // State props
  hasActiveFilters: {
    type: Boolean,
    default: false,
  },
  hasData: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  chartData: {
    type: Object,
    default: null,
  },

  // Summary data
  totalsSummary: {
    type: Object,
    default: null,
  },
  totalSales: {
    type: Number,
    default: 0,
  },
  averagePerTimeUnit: {
    type: Number,
    default: 0,
  },
  bestTimeUnit: {
    type: Object,
    default: () => ({ label: "", amount: 0 }),
  },
  growthRate: {
    type: Number,
    default: 0,
  },

  // Function props
  getChartOptions: {
    type: Function,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
  formatAmount: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(["export-chart"]);

// Get Vuetify theme
const theme = useTheme();
const currentTheme = theme.current.value;
const backgroundColor = currentTheme.colors?.background || "#1e1e1e";

// Chart instance
const chartContainer = ref(null);
let chartInstance = null;
let isChartInitialized = false;

// Define your custom dark theme colors
const customTheme = {
  backgroundColor: backgroundColor,
  textColor: "#ffffff",
  axisLineColor: "#555",
  splitLineColor: "#444",
};

// Safe chart options function
const getSafeChartOptions = () => {
  try {
    if (!props.getChartOptions || typeof props.getChartOptions !== "function") {
      console.error("getChartOptions is not a valid function");
      return getDefaultOptions();
    }

    if (!props.filteredData) {
      console.log("No filtered data, returning default options");
      return getDefaultOptions();
    }

    // Call the getChartOptions function with all required parameters
    const options = props.getChartOptions(customTheme, props.filteredData, props.isDaily);

    // Validate options
    if (!options || typeof options !== "object") {
      console.error("getChartOptions returned invalid options:", options);
      return getDefaultOptions();
    }

    // Ensure series array exists
    if (!options.series || !Array.isArray(options.series)) {
      console.error("Options missing series array");
      options.series = [];
    }

    return options;
  } catch (error) {
    console.error("Error getting chart options:", error);
    return getDefaultOptions();
  }
};

// Default options for "No Data" state
const getDefaultOptions = () => {
  return {
    title: {
      text: props.loading ? "Loading..." : "No Data Available",
      left: "center",
      top: "center",
      textStyle: {
        color: customTheme.textColor,
        fontSize: 16,
      },
    },
    xAxis: {
      show: false,
      type: "category",
    },
    yAxis: {
      show: false,
      type: "value",
    },
    series: [],
  };
};

// Chart functions
const initChart = () => {
  // console.log("initChart called");
  if (!chartContainer.value) {
    console.log("No chart container");
    return;
  }

  if (chartInstance) {
    // console.log("Disposing existing chart instance");
    try {
      chartInstance.dispose();
    } catch (err) {
      console.warn("Error disposing chart:", err);
    }
    chartInstance = null;
  }

  try {
    // console.log("Initializing new chart instance");
    chartInstance = echarts.init(chartContainer.value, "dark");
    isChartInitialized = true;

    // Set initial options
    const initialOptions = getDefaultOptions();
    chartInstance.setOption(initialOptions);

    // console.log("Chart initialized");
  } catch (err) {
    console.error("Failed to initialize chart:", err);
    isChartInitialized = false;
  }
};

const updateChart = () => {
  if (!chartInstance) {
    // console.log("No chart instance, initializing...");
    nextTick(() => {
      initChart();
    });
    return;
  }

  if (props.loading) {
    // console.log("Still loading, showing loading state");
    chartInstance.setOption(getDefaultOptions());
    return;
  }

  if (!props.hasData || !props.filteredData) {
    // console.log('No data available, showing "No Data" message');
    chartInstance.setOption(getDefaultOptions());
    return;
  }

  try {
    const options = getSafeChartOptions();
    // console.log("Setting chart options:", options);

    // Clear chart before setting new options
    chartInstance.clear();

    // Set new options
    chartInstance.setOption(options, {
      notMerge: true,
      replaceMerge: ["series", "xAxis", "yAxis"],
      lazyUpdate: false,
    });

    // Resize chart to fit container
    chartInstance.resize();

    // console.log("Chart updated successfully");
  } catch (err) {
    // console.error("Failed to update chart:", err);
    console.error("Error details:", err.message, err.stack);

    // Fallback to default options
    try {
      chartInstance.setOption(getDefaultOptions());
    } catch (fallbackErr) {
      console.error("Even fallback failed:", fallbackErr);
    }
  }
};

// Expose a method to force chart update
const forceChartUpdate = () => {
  // console.log("Force chart update called");
  updateChart();
};

// Handle window resize
const handleResize = () => {
  if (chartInstance) {
    try {
      chartInstance.resize();
    } catch (err) {
      console.warn("Error resizing chart:", err);
    }
  }
};

// Watchers with debounce to prevent rapid updates
let updateTimeout;
const debouncedUpdateChart = () => {
  clearTimeout(updateTimeout);
  updateTimeout = setTimeout(() => {
    //
    updateChart();
  }, 100);
};

// Watch for changes
watch(() => props.chartType, debouncedUpdateChart);

// Watch filteredData and hasData - trigger update when they change
watch(
  () => props.filteredData,
  (newVal, oldVal) => {
    // Always update when filteredData changes
    debouncedUpdateChart();
  },
  { deep: true },
);

watch(
  () => props.hasData,
  (newVal, oldVal) => {
    debouncedUpdateChart();
  },
);

watch(
  () => props.loading,
  (newVal, oldVal) => {
    debouncedUpdateChart();
  },
);

watch(
  () => props.isDaily,
  () => {
    isChartInitialized = false;
    nextTick(() => {
      initChart();
    });
  },
);

// Lifecycle
onMounted(() => {
  // console.log("BaseSalesChart mounted");
  window.addEventListener("resize", handleResize);

  // Wait for the container to be rendered
  nextTick(() => {
    setTimeout(() => {
      initChart();
      // Initial update
      setTimeout(() => {
        updateChart();
      }, 200);
    }, 100);
  });
});

// Cleanup
onUnmounted(() => {
  if (chartInstance) {
    try {
      chartInstance.dispose();
    } catch (err) {
      console.warn("Error disposing chart:", err);
    }
    chartInstance = null;
  }
  window.removeEventListener("resize", handleResize);
  clearTimeout(updateTimeout);
});

// Expose methods
defineExpose({
  forceChartUpdate,
});
</script>

<template>
  <div>
    <!-- Active Filters Summary -->
    <v-alert
      v-if="hasActiveFilters && isDaily"
      type="info"
      density="compact"
      class="my-4 mx-4"
      closable
    >
      <div v-if="totalsSummary">
        <span class="mr-3"
          >Total amount: <b>{{ formatCurrency(totalsSummary.amount) }}</b></span
        >
        <span class="mr-3"
          >Average per day: <b>{{ formatCurrency(totalsSummary.amount_avg) }}</b> ({{
            totalsSummary.number_of_days
          }}
          days)</span
        >
        <span class="mr-3"
          >Orders: <b>{{ totalsSummary.orders }}</b>
          <span class="text-disabled"> (excluded: {{ totalsSummary.excludedOrders }})</span>
        </span>
        <span
          >Records: <b>{{ totalsSummary.records }}</b></span
        >
      </div>
    </v-alert>

    <v-alert v-if="error" type="error" class="mb-4">
      {{ error }}
    </v-alert>

    <v-alert v-if="!chartData && !loading" type="info" class="mb-4">
      No sales data available for chart. Start by submitting sales records.
    </v-alert>

    <div v-if="loading" class="text-center">
      <v-progress-circular indeterminate></v-progress-circular>
      <div class="mt-2">Loading chart data...</div>
    </div>

    <!-- Always show chart container -->
    <div class="chart-container" :class="{ loading: loading }">
      <!-- Chart will be rendered here -->
      <div ref="chartContainer" style="height: 500px; width: 100%"></div>
    </div>

    <!-- NET sales note -->
    <div v-if="hasData" class="text-caption text-disabled text-right mt-2 mr-4">
      Sales are NET from January 2026 onwards
    </div>

    <!-- Summary Statistics - Only show when we have data -->
    <!-- <v-row v-if="isDev && hasData && filteredData" class="mt-6"> -->
    <!--   <v-col cols="12" md="3"> -->
    <!--     <v-card variant="outlined" class="text-center"> -->
    <!--       <v-card-text> -->
    <!--         <div class="text-h6 text-primary font-weight-bold"> -->
    <!--           {{ formatAmount(totalSales) }} -->
    <!--         </div> -->
    <!--         <div class="text-caption text-medium-emphasis">Total Sales</div> -->
    <!--       </v-card-text> -->
    <!--     </v-card> -->
    <!--   </v-col> -->
    <!--   <v-col cols="12" md="3"> -->
    <!--     <v-card variant="outlined" class="text-center"> -->
    <!--       <v-card-text> -->
    <!--         <div class="text-h6 text-primary font-weight-bold"> -->
    <!--           {{ formatAmount(averagePerTimeUnit) }} -->
    <!--         </div> -->
    <!--         <div class="text-caption text-medium-emphasis"> -->
    <!--           {{ isDaily ? "Daily" : "Monthly" }} Average -->
    <!--         </div> -->
    <!--       </v-card-text> -->
    <!--     </v-card> -->
    <!--   </v-col> -->
    <!--   <v-col cols="12" md="3"> -->
    <!--     <v-card variant="outlined" class="text-center"> -->
    <!--       <v-card-text> -->
    <!--         <div class="text-h6 text-primary font-weight-bold"> -->
    <!--           {{ formatAmount(bestTimeUnit.amount) }} -->
    <!--         </div> -->
    <!--         <div class="text-caption text-medium-emphasis">Best: {{ bestTimeUnit.label }}</div> -->
    <!--       </v-card-text> -->
    <!--     </v-card> -->
    <!--   </v-col> -->
    <!--   <v-col cols="12" md="3"> -->
    <!--     <v-card variant="outlined" class="text-center"> -->
    <!--       <v-card-text> -->
    <!--         <div class="text-h6 text-primary font-weight-bold">{{ growthRate }}%</div> -->
    <!--         <div class="text-caption text-medium-emphasis">Growth Rate</div> -->
    <!--       </v-card-text> -->
    <!--     </v-card> -->
    <!--   </v-col> -->
    <!-- </v-row> -->
  </div>
</template>

<style scoped>
.chart-container {
  position: relative;
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  padding: 0px;
  min-height: 500px;
}

.chart-container.loading {
  opacity: 0.5;
}

.summary-card {
  transition: all 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

:deep(.echarts-for-vue) {
  background-color: #1e1e1e;
}
</style>
